import { useEffect, useMemo, useState } from "react";
import { Download, Info, Layers, Plus, X } from "lucide-react";
import { COMPARE_PALETTE } from "@/lib/utils/daylight-insight/constants";
import { periodAxisLabel, periodLabelWithYear, seriesGranularity } from "@/lib/utils/daylight-insight/dates";
import { downloadCsv } from "@/lib/utils/daylight-insight/records";
import { unitLabel, unitName } from "@/lib/utils/daylight-insight/format";
import type { Bucket, Metric } from "@/lib/utils/daylight-insight/types";
import { useStore } from "@/provider/store";
import { CompareChart, Legend, type CompareSeries } from "@/components/daylight-insight/charts";
import { MultiSelect, OptionSearch } from "@/components/daylight-insight/controls";
import { apiToMetric } from "@/components/daylight-insight/api/metricsAdapt";
import { useMetricRelations } from "@/components/daylight-insight/api/useMetricRelations";
import { useMetricBranchTrend } from "@/components/daylight-insight/api/useMetricBranchTrend";
import type { DashboardMetric, MetricRelationsParams } from "@/components/daylight-insight/api/types";
import type { RelationBase } from "./MetricTrendsPanel";

/** The config filter key the compared branches travel under in `@Filters`. */
const BRANCH_KEY = "branch";

/** Up to four series on one chart (matches the seeded drill's compare cap). */
const MAX_SERIES = 4;

type Mode = "branches" | "metrics";

/** Compare across Branches / Metrics segmented toggle. */
function CompareToggle({ mode, setMode }: { mode: Mode; setMode: (m: Mode) => void }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div className="cmp-lbl" style={{ marginBottom: 6 }}>Compare across</div>
      <div className="dl-seg cmp-seg">
        <button className={mode === "branches" ? "on" : ""} onClick={() => setMode("branches")}>Branches</button>
        <button className={mode === "metrics" ? "on" : ""} onClick={() => setMode("metrics")}>Metrics</button>
      </div>
    </div>
  );
}

/** Trim + case-fold a metric code so the SP's `MetricCode` and the dashboard's
 *  own code match even when they differ in casing or CHAR padding. Both sides
 *  of every code comparison in this file go through it — an unnormalized
 *  lookup misses silently and exports a blank column rather than an error. */
const normCode = (v: unknown): string => String(v ?? "").trim().toLowerCase();

/** A metric NAME turned into a filename fragment: letters/digits kept, every
 *  run of anything else collapsed to a dash ("M0102 SOC Date Capture %" →
 *  "M0102-SOC-Date-Capture"). Falls back to the code when a name slugs empty. */
function fileSlug(name: string, fallback: string): string {
  const slug = name.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return slug || fallback.replace(/[^A-Za-z0-9]+/g, "-");
}

/** Export the compared series as a CSV: one column per series, one row per
 *  period (Period, <series 1>, <series 2>, …).
 *
 *  Rows are keyed off `isoPeriods` — the same axis every series' buckets are
 *  built from, so index `i` lines up — which lets the Period column carry its
 *  year ("May '25") rather than the space-saving month-only chart label.
 *
 *  Headers are de-duplicated: two series can carry the same label (a metric
 *  whose name did not resolve falls back to its code, and two branches can
 *  share a name), and identical headers make the columns unreadable — and
 *  unpivotable — once the file leaves the app. */
function exportCompareCsv(series: CompareSeries[], isoPeriods: string[], filename: string): void {
  // The grain comes off the axis' own spacing — a drill opened from the All
  // Metrics Week/Day views compares weekly/daily buckets, which a month-only
  // Period column would collapse into twelve identical rows.
  const grain = seriesGranularity(isoPeriods);
  const used = new Map<string, number>();
  const headers = series.map((x) => {
    const n = (used.get(x.key) ?? 0) + 1;
    used.set(x.key, n);
    return n > 1 ? `${x.key} (${n})` : x.key;
  });
  const rows: unknown[][] = [["Period", ...headers]];
  isoPeriods.forEach((iso, i) =>
    rows.push([periodLabelWithYear(iso, grain), ...series.map((x) => x.buckets[i]?.value ?? "")]),
  );
  downloadCsv(filename, rows);
}

/**
 * Compare View for the metric drill-down — enabled for every pillar.
 *
 * Branch mode: the Branch dropdown is fed by the branch filter options
 * (`accessList`), defaulting to the first branch so data shows immediately; its
 * lines are illustrative (per-branch trend backend is TBD).
 *
 * Metric mode: the drilled metric is the subject; the dropdown lists the current
 * pillar's metrics. Every selected metric's REAL trend comes from one
 * `metric-relations` call (it supports multiple `metricCodes`), read off the
 * response's `MetricTrendValues`. Metrics are aligned on a shared period axis;
 * periods a metric has no value for are left as gaps.
 */
export function DrillCompareView({
  subject,
  subjectBuckets,
  metrics,
  relationBase,
  onExit,
}: {
  subject: Metric;
  subjectBuckets: Bucket[];
  metrics: DashboardMetric[];
  relationBase: RelationBase;
  onExit: () => void;
}) {
  const s = useStore();
  const [mode, setMode] = useState<Mode>("branches");
  // Default the branch selection to the first option so the chart loads with data.
  const [branches, setBranches] = useState<string[]>(() => s.accessList.slice(0, 1));
  const [compareCodes, setCompareCodes] = useState<string[]>([]);
  const [pickOpen, setPickOpen] = useState(false);
  // Add-metric search — cleared whenever the picker closes so it reopens whole.
  const [pickQuery, setPickQuery] = useState("");
  useEffect(() => { if (!pickOpen) setPickQuery(""); }, [pickOpen]);

  const dmByCode = useMemo(() => {
    const m = new Map<string, DashboardMetric>();
    for (const dm of metrics) m.set(dm.code, dm);
    return m;
  }, [metrics]);
  const kindByCode = useMemo(() => {
    const m = new Map<string, Metric["kind"]>();
    for (const dm of metrics) m.set(dm.code, apiToMetric(dm).kind);
    return m;
  }, [metrics]);

  const selectedCodes = useMemo(
    () => [subject.id, ...compareCodes].slice(0, MAX_SERIES),
    [subject.id, compareCodes],
  );

  // Metric mode: fetch every selected metric's trend in one relations call
  // (comma-joined codes). Skipped in branch mode. The hook keys on the params'
  // JSON, so a re-created object with identical content won't re-fire.
  const metricRelParams: MetricRelationsParams | null = useMemo(
    () => (mode === "metrics" ? { ...relationBase, metricCodes: selectedCodes.join(",") } : null),
    [mode, relationBase, selectedCodes],
  );
  const { trendValues, loading: relLoading } = useMetricRelations(metricRelParams);

  // Shared period axis + per-code value lookup, from `MetricTrendValues`.
  //
  // Only rows belonging to a SELECTED metric count. The relations SP answers
  // with the related metrics' trends too, and folding those into the axis adds
  // periods no selected series has a value for — rows that read as a gap on the
  // chart and as an empty cell in the CSV.
  //
  // A row with no `MetricCode` is attributed to the subject when the subject is
  // the only metric asked for: the SP drops the column on a single-code request,
  // and without this the compare chart is empty for a metric whose Chart tab
  // (which applies the same tolerance) plots fine.
  const metricAxis = useMemo(() => {
    const wanted = new Set(selectedCodes.map(normCode));
    const soleCode = selectedCodes.length === 1 ? normCode(selectedCodes[0]) : null;
    const byCode = new Map<string, Map<string, number>>();
    const periods = new Set<string>();
    for (const t of trendValues) {
      if (!t.SummaryPeriodStartDate) continue;
      const code = normCode(t.MetricCode) || soleCode;
      const v = t.MetricValue;
      if (!code || !wanted.has(code) || v == null) continue;
      const n = Number(v);
      if (!Number.isFinite(n)) continue;
      const iso = String(t.SummaryPeriodStartDate);
      periods.add(iso);
      if (!byCode.has(code)) byCode.set(code, new Map());
      byCode.get(code)!.set(iso, n);
    }
    return { isoPeriods: [...periods].sort(), byCode };
  }, [trendValues, selectedCodes]);

  // Branch mode: fetch the subject metric's trend per selected branch. Branch
  // names → SystemIDs, sent as `filters.branch` (replacing the page's own branch
  // selection, the other filters still apply); the response keys rows by BranchCode, which we
  // map back to the branch label for the legend. Skipped in metric mode.
  const selectedBranchIds = useMemo(
    () => branches.map((n) => s.branchIdFor(n)).filter((v): v is string => !!v),
    [branches, s],
  );
  const branchTrendParams: MetricRelationsParams | null = useMemo(
    () => (mode === "branches" && selectedBranchIds.length
      ? {
          ...relationBase,
          filters: { ...relationBase.filters, [BRANCH_KEY]: selectedBranchIds },
          metricCodes: subject.id,
        }
      : null),
    [mode, relationBase, selectedBranchIds, subject.id],
  );
  const { trendValues: branchTrend, loading: branchLoading } = useMetricBranchTrend(branchTrendParams);

  const labelByBranchCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const b of s.branchOptions) if (b.branchCode) m.set(b.branchCode.trim().toUpperCase(), b.label);
    return m;
  }, [s.branchOptions]);

  // Shared period axis + per-branch value lookup, from the branch-trend response.
  const branchAxis = useMemo(() => {
    const rows = branchTrend.filter((t) => t.SummaryPeriodStartDate && t.BranchCode);
    const isoPeriods = [...new Set(rows.map((t) => String(t.SummaryPeriodStartDate)))].sort();
    const byCode = new Map<string, Map<string, number>>();
    const order: string[] = [];
    for (const t of rows) {
      const code = String(t.BranchCode ?? "").trim();
      const v = t.MetricValue;
      if (!code || v == null) continue;
      const n = Number(v);
      if (!Number.isFinite(n)) continue;
      if (!byCode.has(code)) { byCode.set(code, new Map()); order.push(code); }
      byCode.get(code)!.set(String(t.SummaryPeriodStartDate), n);
    }
    return { isoPeriods, byCode, order };
  }, [branchTrend]);

  const banner = (
    <div className="cmp-banner">
      <span>
        <Info size={15} style={{ color: "var(--hermes)", flexShrink: 0 }} />
        Compare mode overrides your branch filter — showing only the {mode === "metrics" ? "metrics" : "branches"} selected below.
      </span>
      <button onClick={onExit}>Exit compare</button>
    </div>
  );

  if (!subjectBuckets.length) {
    return (
      <>
        {banner}
        <CompareToggle mode={mode} setMode={setMode} />
        <div className="cmp-banner"><span>No trend data to compare for this metric.</span></div>
      </>
    );
  }

  // ---------- BRANCHES MODE ----------
  if (mode === "branches") {
    // Real per-branch trend of the subject metric, aligned on a shared period
    // axis; branches with no value at a period are left as gaps.
    const branchGrain = seriesGranularity(branchAxis.isoPeriods);
    const series: CompareSeries[] = branchAxis.order.slice(0, MAX_SERIES).map((code, i) => ({
      key: labelByBranchCode.get(code.toUpperCase()) ?? code,
      color: COMPARE_PALETTE[i % COMPARE_PALETTE.length],
      kind: subject.kind,
      buckets: branchAxis.isoPeriods.map((iso) => ({ label: periodAxisLabel(iso, branchGrain), value: branchAxis.byCode.get(code)?.get(iso) ?? null })),
    }));
    const hasData = branchAxis.isoPeriods.length > 0;
    return (
      <>
        {banner}
        <CompareToggle mode={mode} setMode={setMode} />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <MultiSelect
            label="Branches"
            icon={<Layers size={14} style={{ color: "var(--ink-3)" }} />}
            options={s.accessList}
            selected={branches}
            onChange={(sel) => setBranches(sel.length ? sel.slice(0, MAX_SERIES) : s.accessList.slice(0, 1))}
            allLabel="All branches"
          />
          <button
            className="dl-btn"
            disabled={!hasData}
            title={hasData ? "Export this comparison" : "Nothing to export yet"}
            onClick={() => exportCompareCsv(series, branchAxis.isoPeriods, `DayLight_Compare_${fileSlug(subject.name, subject.id)}_branches.csv`)}
          ><Download size={13} />Export</button>
        </div>
        {branchLoading && !hasData ? (
          <div className="cmp-banner"><span>Loading comparison…</span></div>
        ) : !hasData ? (
          <div className="cmp-banner"><span>No trend data for the selected branches.</span></div>
        ) : (
          <>
            <CompareChart series={series} />
            <Legend items={series.map((x) => ({ label: x.key, color: x.color }))} />
          </>
        )}
      </>
    );
  }

  // ---------- METRICS MODE ----------
  const selKinds = [...new Set(selectedCodes.map((code) => (code === subject.id ? subject.kind : kindByCode.get(code))))].filter(Boolean) as Metric["kind"][];
  const axisFull = selKinds.length >= 2;
  const rightKind = selKinds[1];
  const sideOf = (k: Metric["kind"]) => (k === selKinds[0] ? "L" : "R");

  const metricGrain = seriesGranularity(metricAxis.isoPeriods);
  const bucketsFor = (code: string) =>
    metricAxis.isoPeriods.map((iso) => ({
      label: periodAxisLabel(iso, metricGrain),
      value: metricAxis.byCode.get(normCode(code))?.get(iso) ?? null,
    }));
  const series: CompareSeries[] = selectedCodes.map((code, i) => {
    const isSubj = code === subject.id;
    return {
      id: code,
      key: isSubj ? subject.name : dmByCode.get(code)?.name ?? code,
      color: COMPARE_PALETTE[i % COMPARE_PALETTE.length],
      kind: isSubj ? subject.kind : kindByCode.get(code) ?? subject.kind,
      buckets: bucketsFor(code),
    };
  });
  const hasData = metricAxis.isoPeriods.length > 0;

  // Add-metric pool: the pillar's other metrics, blocked when they'd introduce a
  // third distinct scale.
  const pickQ = pickQuery.trim().toLowerCase();
  const pool = metrics
    .filter((m) => !selectedCodes.includes(m.code))
    .filter((m) => !pickQ || m.name.toLowerCase().includes(pickQ))
    .map((m) => ({ dm: m, blocked: axisFull && !selKinds.includes(kindByCode.get(m.code)!) }));

  return (
    <>
      {banner}
      <CompareToggle mode={mode} setMode={setMode} />
      <div className="cmp-subject">
        <span className="cmp-lbl">Subject</span>
        <span className="cmp-chip subject">
          <span className="cmp-dot" style={{ background: COMPARE_PALETTE[0] }} />
          {subject.name}
          {axisFull && <span className="cmp-axis">{sideOf(subject.kind)}</span>}
        </span>
      </div>
      <div className="cmp-compareto">
        <span className="cmp-lbl">Compare to</span>
        <div className="cmp-chips">
          {compareCodes.map((code, i) => (
            <span key={code} className="cmp-chip">
              <span className="cmp-dot" style={{ background: COMPARE_PALETTE[(i + 1) % COMPARE_PALETTE.length] }} />
              {dmByCode.get(code)?.name ?? code}
              {axisFull && <span className="cmp-axis">{sideOf(kindByCode.get(code)!)}</span>}
              <button onClick={() => setCompareCodes(compareCodes.filter((x) => x !== code))} aria-label="remove"><X size={12} /></button>
            </span>
          ))}
          {selectedCodes.length < MAX_SERIES && (
            <div style={{ position: "relative", display: "inline-block" }}>
              <button className="cmp-add" onClick={() => setPickOpen(!pickOpen)}><Plus size={13} />Add metric</button>
              {pickOpen && (
                <div className="dl-pop cmp-pop">
                  <OptionSearch query={pickQuery} setQuery={setPickQuery} placeholder="Search metrics…" />
                  {axisFull && <div className="cmp-poptip">A chart can show 2 scales ({unitLabel(selKinds[0])} and {unitLabel(rightKind)}). Other units are disabled.</div>}
                  {pool.map(({ dm, blocked }) => (
                    <div
                      key={dm.code}
                      className={"dl-opt" + (blocked ? " disabled" : "")}
                      onClick={() => { if (blocked) return; setCompareCodes([...compareCodes, dm.code]); setPickOpen(false); }}
                    >
                      <span style={{ flex: 1 }}>{dm.name}</span>
                      <span style={{ fontSize: 10.5, color: blocked ? "var(--red)" : "var(--ink-3)", textTransform: "uppercase", letterSpacing: ".03em" }}>{blocked ? "diff. scale" : kindByCode.get(dm.code)}</span>
                    </div>
                  ))}
                  {pool.length === 0 && <div className="dl-noopt">{pickQ ? `No metrics match “${pickQuery.trim()}”.` : "No other metrics to compare."}</div>}
                </div>
              )}
            </div>
          )}
          <span className="cmp-count">{selectedCodes.length} of {MAX_SERIES} max{axisFull ? " · 2 scales" : ""}</span>
        </div>
      </div>
      {relLoading && !hasData ? (
        <div className="cmp-banner"><span>Loading comparison…</span></div>
      ) : !hasData ? (
        <div className="cmp-banner"><span>No trend data for the selected metrics.</span></div>
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
            <button className="dl-btn" onClick={() => exportCompareCsv(series, metricAxis.isoPeriods, `DayLight_Compare_${fileSlug(subject.name, subject.id)}_metrics.csv`)}><Download size={13} />Export</button>
          </div>
          <CompareChart series={series} />
          <Legend items={series.map((x) => ({ label: x.key, color: x.color }))} />
          {axisFull && (
            <div className="cmp-note">
              <Info size={13} />
              Two scales in play — left axis is in {unitName(selKinds[0])}, right axis in {unitName(rightKind)}. A chart shows at most two.
            </div>
          )}
        </>
      )}
    </>
  );
}
